import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import { AxiosError } from "axios";
import React, { useState } from "react";
import { Controller } from "react-hook-form";

import { useBFFProductService } from "@/api/hooks/productService.hook";
import { MultiFileUploadFieldChildrenProps } from "@/components/Form/RHF/FormFields/MultiUploadField/multiUploadField.component";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { MessageComponent } from "@/components/General/Messages/messages.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import {
    MAX_FILE_UPLOADS,
    WriteProgressiveReviewFormValues
} from "../../writeReview.schema";

type PhotoUploadFieldProps =
    MultiFileUploadFieldChildrenProps<WriteProgressiveReviewFormValues> & {
        staticPhotoUrls: Array<string>;
    };

const PhotoUploadField = ({
    fields,
    control,
    disabled,
    name,
    actions,
    staticPhotoUrls
}: PhotoUploadFieldProps) => {
    const { t } = useGranite();
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    const [error, setError] = useState<undefined | string>();

    const productService = useBFFProductService();

    const reachedMaxUploads = fields.length >= MAX_FILE_UPLOADS;

    const isRemovable = (photoUrl: string) => {
        return !staticPhotoUrls.includes(photoUrl);
    };

    const selectUpload = (index: number) => {
        setSelectedIndex(index);
    };

    const handleOnRemove = (index: number) => () => {
        actions.remove(index);
    };

    const handleUploadChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];

        if (!file) return;

        const formData = new FormData();
        formData.set("photo", file);

        try {
            const { data } = await productService.post<{
                id: string;
                url: string;
            }>("/reviews/uploadPhoto", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            actions.append({ url: data.url, caption: "" });

            setError(undefined);
        } catch (error: any) {
            if (error instanceof AxiosError) {
                const axiosError = error as AxiosError<{ errors: string }>;

                if (axiosError.response?.data.errors) {
                    setError(axiosError.response?.data.errors);
                }
            }
        }
    };

    return (
        <>
            {error && (
                <MessageComponent
                    message={{
                        dismissible: false,
                        id: "photo-upload-field-error",
                        status: "danger",
                        text: error
                    }}
                />
            )}
            <div className={cn("tw:flex tw:gap-4")}>
                {fields.map((fieldItem, index) => {
                    return (
                        <div
                            key={fieldItem.id}
                            className={cn(
                                "tw:border tw:border-dashed tw:border-grey-input tw:w-16 tw:h-16",
                                {
                                    "tw:outline-2 tw:outline-primary tw:-outline-offset-1":
                                        selectedIndex === index
                                }
                            )}
                        >
                            <Controller
                                control={control}
                                name={`${name}.${index}.url`}
                                render={() => {
                                    return (
                                        <div
                                            className={cn(
                                                "tw:group/photo-upload-image",
                                                "tw:relative"
                                            )}
                                        >
                                            <img
                                                className={cn(
                                                    "tw:w-16 tw:h-16 tw:object-cover"
                                                )}
                                                src={fieldItem.url}
                                                alt={fieldItem.caption ?? ""}
                                                onClick={() =>
                                                    selectUpload(index)
                                                }
                                            />
                                            {isRemovable(fieldItem.url) && (
                                                <Button
                                                    className={cn(
                                                        "tw:hidden tw:rounded-full tw:h-6 tw:w-6 tw:p-0 tw:justify-center tw:absolute tw:items-center tw:top-0 tw:right-0 tw:translate-x-1/2 tw:-translate-y-1/2",
                                                        "tw:group-hover/photo-upload-image:flex"
                                                    )}
                                                    onClick={handleOnRemove(
                                                        index
                                                    )}
                                                >
                                                    <i
                                                        className={cn(
                                                            "las la-times"
                                                        )}
                                                    />
                                                </Button>
                                            )}
                                        </div>
                                    );
                                }}
                            />
                        </div>
                    );
                })}
                {!reachedMaxUploads && (
                    <div
                        className={cn(
                            "tw:border tw:border-dashed tw:border-grey-input tw:h-16 tw:min-w-16"
                        )}
                    >
                        <label
                            htmlFor={`${name}.${fields.length}.url`}
                            className={cn(
                                "tw:h-16 tw:min-w-16 tw:block tw:text-center tw:text-3xl tw:leading-16 tw:cursor-pointer tw:p-0 tw:m-0"
                            )}
                        >
                            <i className={cn("las la-plus")} />
                        </label>
                        <input
                            id={`${name}.${fields.length}.url`}
                            disabled={disabled}
                            type={"file"}
                            hidden={true}
                            onChange={handleUploadChange}
                            accept={".jpg,.jpeg,.png,.heic"}
                        />
                    </div>
                )}
            </div>
            {selectedIndex !== null && (
                <Row>
                    <Col>
                        <TextField
                            key={`${name}.${selectedIndex}.caption`}
                            name={`${name}.${selectedIndex}.caption`}
                            label={t(
                                "product.reviews.form.photos.caption.label"
                            )}
                            placeholder={t(
                                "product.reviews.form.photos.caption.placeholder"
                            )}
                            required={false}
                            disabled={disabled}
                        />
                    </Col>
                </Row>
            )}
        </>
    );
};

export { PhotoUploadField };
